
"use client";

import { useState, useMemo, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import type { Product, Category, Brand, StoreSettings } from '@/lib/types';
import { Loader2, Search } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { ProductCard } from '@/components/shared/ProductCard';
import { Input } from '@/components/ui/input';
import { useSearchParams, useRouter } from 'next/navigation';
import { db } from '@/lib/firebase';


const PuffRanges = [
    { label: 'Até 5.000', value: 5000 },
    { label: 'Até 10.000', value: 10000 },
    { label: 'Até 15.000', value: 15000 },
    { label: 'Até 25.000', value: 25000 },
    { label: 'Até 30.000', value: 30000 },
    { label: 'Até 40.000', value: 40000 },
];

export default function ProductsGridAndFilters({ initialCategory, searchQuery, preloadedProducts }: { initialCategory?: string, searchQuery?: string | null, preloadedProducts?: Product[] }) {
    const searchParams = useSearchParams();
    const router = useRouter();
    
    const [allProducts, setAllProducts] = useState<Product[]>(preloadedProducts || []);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [priceRange, setPriceRange] = useState<[number]>([500]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategory ? [initialCategory] : []);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedPuffs, setSelectedPuffs] = useState<number[]>([]);
    
    const currentSearchQuery = searchQuery ?? searchParams.get('q');
    const [searchInputValue, setSearchInputValue] = useState(currentSearchQuery || '');
     
    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        router.push(`/products?q=${encodeURIComponent(searchInputValue)}`);
    };

    useEffect(() => {
        setSearchInputValue(currentSearchQuery || '');
    }, [currentSearchQuery]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (!preloadedProducts) {
                    const productsCollection = collection(db, 'products');
                    const productsSnapshot = await getDocs(productsCollection);
                    setAllProducts(productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product)));
                }

                const categoriesCollection = collection(db, 'categories');
                const categoriesSnapshot = await getDocs(categoriesCollection);
                setCategories(categoriesSnapshot.docs.map(doc => doc.data() as Category));

                const brandsCollection = collection(db, 'brands');
                const brandsSnapshot = await getDocs(brandsCollection);
                setBrands(brandsSnapshot.docs.map(doc => doc.data() as Brand));
                
                const storeSettingsDoc = await getDoc(doc(db, 'settings', 'store'));
                if (storeSettingsDoc.exists()) {
                    setStoreSettings(storeSettingsDoc.data() as StoreSettings);
                }

            } catch (error) {
                console.error("Error fetching data for products page:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [preloadedProducts]);

    const filteredAndSortedProducts = useMemo(() => {
        let filtered = allProducts;

        if (currentSearchQuery) {
            const searchTerms = currentSearchQuery.toLowerCase().split(' ').filter(Boolean);
            filtered = filtered.filter(p => {
                const productText = [
                    p.name.toLowerCase(),
                    p.brand?.toLowerCase() || '',
                    p.category?.toLowerCase() || '',
                    p.puffs?.toString() || '',
                    ...(p.tags || []).map(t => t.toLowerCase()),
                    ...(p.variants?.map(v => v.options.name.toLowerCase()) || [])
                ].join(' ');

                return searchTerms.every(term => {
                    const kValue = term.match(/^(\d+)k$/);
                    if (kValue) {
                        const numericValue = parseInt(kValue[1]) * 1000;
                        return productText.includes(String(numericValue));
                    }
                    return productText.includes(term);
                });
            });
        }


        filtered = filtered.filter(p => p.price <= priceRange[0]);

        if (selectedCategories.length > 0) {
            filtered = filtered.filter(p => selectedCategories.includes(p.category));
        }

        if (selectedBrands.length > 0) {
            filtered = filtered.filter(p => p.brand && selectedBrands.includes(p.brand));
        }

        if (selectedPuffs.length > 0) {
            filtered = filtered.filter(p => p.puffs && selectedPuffs.some(range => p.puffs! <= range));
        }

        // Sort by stock status (in-stock first) then by position
        return filtered.sort((a, b) => {
            const aStock = a.variants ? a.variants.reduce((acc, v) => acc + v.stock, 0) : a.stock;
            const bStock = b.variants ? b.variants.reduce((acc, v) => acc + v.stock, 0) : b.stock;
            if (aStock > 0 && bStock === 0) return -1;
            if (aStock === 0 && bStock > 0) return 1;
            return (a.position || 999) - (b.position || 999);
        });

    }, [currentSearchQuery, priceRange, selectedCategories, selectedBrands, selectedPuffs, allProducts]);


    const handleCategoryChange = (categoryName: string) => {
        setSelectedCategories(prev =>
            prev.includes(categoryName)
                ? prev.filter(c => c !== categoryName)
                : [...prev, categoryName]
        );
    };

    const handleBrandChange = (brandName: string) => {
        setSelectedBrands(prev =>
            prev.includes(brandName)
                ? prev.filter(b => b !== brandName)
                : [...prev, brandName]
        );
    };
    
    const handlePuffChange = (puffValue: number) => {
        setSelectedPuffs(prev =>
            prev.includes(puffValue)
                ? prev.filter(p => p !== puffValue)
                : [...prev, puffValue]
        );
    };
    
    if (isLoading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-16 w-16 animate-spin" />
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <aside className="col-span-1 md:col-span-1">
                <h2 className="text-2xl font-bold font-headline mb-4">Filtros</h2>
                <Accordion type="multiple" defaultValue={['price', 'category']} className="w-full">
                    <AccordionItem value="price">
                        <AccordionTrigger className="font-semibold">Preço</AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                            <Slider
                                defaultValue={[500]}
                                max={500}
                                step={10}
                                onValueChange={(value) => setPriceRange(value as [number])}
                            />
                            <div className="flex justify-between text-sm">
                                <span>R$0</span>
                                <span>R${priceRange[0]}</span>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="category">
                        <AccordionTrigger className="font-semibold">Categorias</AccordionTrigger>
                        <AccordionContent className="space-y-2 pt-2">
                            {categories.map(category => (
                                <div key={category.id} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`cat-${category.id}`}
                                        onCheckedChange={() => handleCategoryChange(category.name)}
                                        checked={selectedCategories.includes(category.name)}
                                    />
                                    <Label htmlFor={`cat-${category.id}`}>{category.name}</Label>
                                </div>
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                     <AccordionItem value="brand">
                        <AccordionTrigger className="font-semibold">Marcas</AccordionTrigger>
                        <AccordionContent className="space-y-2 pt-2">
                            {brands.map(brand => (
                                <div key={brand.id} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`brand-${brand.id}`}
                                        onCheckedChange={() => handleBrandChange(brand.name)}
                                        checked={selectedBrands.includes(brand.name)}
                                    />
                                    <Label htmlFor={`brand-${brand.id}`}>{brand.name}</Label>
                                </div>
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                     <AccordionItem value="puffs">
                        <AccordionTrigger className="font-semibold">Puffs</AccordionTrigger>
                        <AccordionContent className="space-y-2 pt-2">
                            {PuffRanges.map(range => (
                                <div key={range.value} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`puff-${range.value}`}
                                        onCheckedChange={() => handlePuffChange(range.value)}
                                        checked={selectedPuffs.includes(range.value)}
                                    />
                                    <Label htmlFor={`puff-${range.value}`}>{range.label}</Label>
                                </div>
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </aside>
            <main className="col-span-1 md:col-span-3">
                 <form onSubmit={handleSearch} className="relative mb-8">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input 
                        name="search" 
                        placeholder="Buscar por nome, sabor, marca..." 
                        className="pl-12 h-12 text-lg" 
                        value={searchInputValue}
                        onChange={e => setSearchInputValue(e.target.value)}
                    />
                </form>
                <h1 className="text-3xl font-bold font-headline mb-8">
                    {initialCategory || (currentSearchQuery ? `Resultados para: "${currentSearchQuery}"` : "Todos os Produtos")}
                </h1>
                 {filteredAndSortedProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {filteredAndSortedProducts.map(product => (
                            <ProductCard key={product.id} product={{ ...product, storeSettings }} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <h2 className="text-2xl font-bold font-headline">Nenhum produto encontrado</h2>
                        <p className="text-muted-foreground mt-2">
                            Tente ajustar seus filtros ou termo de busca.
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}
